import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api, fetchCollection } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { SupportBox } from "@/components/SupportSection";

export default function PaymentResultPage({ cancelled = false, submitted = false, support = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(submitted ? "submitted" : cancelled ? "cancelled" : "checking");
  const stats = useQuery({ queryKey: ["stats"], queryFn: () => fetchCollection("/stats") });
  const paymentsEnabled = Boolean(stats.data?.payments_enabled);

  useEffect(() => {
    if (cancelled || submitted || !sessionId) return undefined;
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        setState("failed");
        return;
      }
      attempts += 1;
      if (attempts < 8) timer = setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancelled, submitted, sessionId]);

  const content = {
    checking: { icon: Clock, title: "Confirmando el pago", text: "Estamos verificando el pago con Stripe. No cierres esta ventana.", testId: "payment-checking" },
    paid: support
      ? { icon: CheckCircle2, title: "¡Gracias por apoyar TODOtiro!", text: "Tu aportación ayuda a mantener el calendario, las crónicas y la hemeroteca. Si es una suscripción, puedes cancelarla en cualquier momento desde el correo de Stripe.", testId: "support-success" }
      : { icon: CheckCircle2, title: "Pago confirmado", text: "Tu tirada ya está en revisión. La redacción ha recibido el aviso en el móvil y, en cuanto la apruebe, el cartel aparecerá en la portada hasta el día de la tirada.", testId: "payment-success" },
    submitted: { icon: CheckCircle2, title: "Tirada enviada", text: "Tu tirada ya está en revisión. La redacción ha recibido el aviso en el móvil y, en cuanto la apruebe, el cartel aparecerá en la portada hasta el día de la tirada. Publicar es gratis.", testId: "sponsored-submitted" },
    failed: { icon: XCircle, title: "El pago no se completó", text: "No se ha realizado ningún cargo. Puedes volver a intentarlo desde el formulario de patrocinio.", testId: "payment-failed" },
    timeout: { icon: Clock, title: "Pago pendiente", text: "Stripe aún no ha confirmado el pago. Si se ha realizado el cargo, la tirada quedará en revisión automáticamente en unos minutos.", testId: "payment-timeout" },
    cancelled: { icon: XCircle, title: "Pago cancelado", text: support ? "No se ha realizado ningún cargo. Gracias igualmente por pensar en apoyar la web." : "Has cancelado el pago. No se ha realizado ningún cargo y puedes volver a enviar la tirada cuando quieras.", testId: "payment-cancelled" },
  }[state];
  const Icon = content.icon;

  return (
    <main className="grid min-h-screen place-items-center gap-10 bg-charcoal px-5 py-16 text-slate-100" data-testid="payment-result-page">
      <div className="poster-card max-w-xl p-8 text-center sm:p-12" data-testid={content.testId}>
        <Icon className={`mx-auto mb-6 h-12 w-12 ${state === "paid" ? "text-clay" : state === "checking" ? "animate-pulse text-brass" : "text-red-300"}`} />
        <p className="eyebrow">{support || state === "submitted" ? (support ? "Apoya TODOtiro" : "Tu tirada en portada") : "Tirada patrocinada"}</p>
        <h1 className="mt-4 font-display text-5xl uppercase leading-none">{content.title}</h1>
        <p className="mt-6 text-base leading-8 text-slate-300">{content.text}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild className="rounded-none bg-clay font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass"><Link to="/" data-testid="payment-home-link">Volver a la portada</Link></Button>
          {state !== "paid" && state !== "checking" && state !== "submitted" ? <Button asChild variant="outline" className="rounded-none border-white/15 bg-transparent font-mono text-xs uppercase tracking-[0.22em]"><Link to={support ? "/#apoyo" : "/#patrocinio"} data-testid="payment-retry-link">Volver</Link></Button> : null}
        </div>
      </div>
      {state === "submitted" && paymentsEnabled ? (
        <div className="w-full max-w-4xl" data-testid="submitted-support">
          <p className="eyebrow mb-4 text-center">¿Te ayuda TODOtiro? Apóyala si quieres</p>
          <SupportBox />
        </div>
      ) : null}
    </main>
  );
}
